'use client'

import { cn } from '@blogging/utils'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

interface SmoothLoaderProps {
  children?: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg'
  showDelay?: number
}

export function SmoothLoader({
  children,
  className,
  size = 'md',
  showDelay = 200,
}: SmoothLoaderProps) {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), showDelay)
    return () => clearTimeout(timer)
  }, [showDelay])

  if (!show) return null

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-2 animate-in fade-in-0 duration-200',
        className,
      )}
    >
      <Loader2 className={cn('animate-spin text-primary', sizeClasses[size])} />
      {children && (
        <span className="text-sm text-muted-foreground">{children}</span>
      )}
    </div>
  )
}

interface PageTransitionProps {
  isLoading: boolean
  children: React.ReactNode
  fallback?: React.ReactNode
  className?: string
}

export function PageTransition({
  isLoading,
  children,
  fallback,
  className,
}: PageTransitionProps) {
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center min-h-[200px]',
          className,
        )}
      >
        {fallback || <SmoothLoader>Loading...</SmoothLoader>}
      </div>
    )
  }

  return (
    <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
      {children}
    </div>
  )
}
